import type { IDescriptionSection } from '@atlas/types'
import { cn } from '@atlas/ui-primitives'
import { Eyebrow } from '@/components/editorial/PageHeader'
import { ExternalHtml } from './ExternalHtml'
import { FromWorkfrontBadge } from './WorkfrontBadges'

/** What joins a brief's context to its group's name in a section heading. */
const CONTEXT_SEPARATOR = ' › '

/** Consecutive sections that share one context, such as a brief's form groups. */
interface SectionGroup {
	context: string | null
	sections: { key: string; title: string; fields: IDescriptionSection['fields'] }[]
}

/**
 * The sections grouped under their context. A heading like `Brief: Banner › Context`
 * is a brief's group: `Brief: Banner` heads the run of sections it shares, once, and
 * `Context` heads the section, so the page does not repeat the joined path.
 */
function groupSections(sections: IDescriptionSection[]): SectionGroup[] {
	const groups: SectionGroup[] = []
	for (const section of sections) {
		const at = section.heading.lastIndexOf(CONTEXT_SEPARATOR)
		const context = at < 0 ? null : section.heading.slice(0, at)
		const title = at < 0 ? section.heading : section.heading.slice(at + CONTEXT_SEPARATOR.length)
		const last = groups.at(-1)
		const group: SectionGroup = last && last.context === context ? last : { context, sections: [] }
		if (group !== last) groups.push(group)
		// Positional, so two sections of one heading (a form group named like Overview) stay distinct.
		group.sections.push({
			key: `${groups.length}:${group.sections.length}:${section.heading}`,
			title,
			fields: section.fields,
		})
	}
	return groups
}

function SectionFields({ fields }: { fields: IDescriptionSection['fields'] }) {
	return (
		<dl className="space-y-2">
			{fields.map((field) => (
				<div key={field.label}>
					<dt className="font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle">{field.label}</dt>
					<dd>
						<ExternalHtml html={field.html} />
					</dd>
				</div>
			))}
		</dl>
	)
}

/**
 * The description sections Workfront wrote, read-only and in Workfront's order. They sit
 * above the ATLAS briefing and never replace it: the briefing is where PMs write for
 * designers, and the sync must not erase that. Nothing renders without a section.
 */
export function ExternalDescription({ sections }: { sections: IDescriptionSection[] | null }) {
	const groups = groupSections((sections ?? []).filter((section) => section.fields.length > 0))
	if (groups.length === 0) return null
	return (
		<section aria-label="Workfront description" className="mt-6">
			<div className="mb-2 flex items-center gap-2">
				<Eyebrow>Workfront description</Eyebrow>
				<FromWorkfrontBadge />
			</div>
			<div className="space-y-3 rounded-ds-md bg-ds-surface p-3">
				{groups.map((group) => (
					<div
						key={group.sections[0].key}
						className={cn('space-y-3', group.context && 'border-t border-ds-border pt-3 first:border-t-0 first:pt-0')}
					>
						{group.context && <h3 className="text-sm font-semibold text-ds-text">{group.context}</h3>}
						{group.sections.map((section) => {
							const Heading = group.context ? 'h4' : 'h3'
							return (
								<div key={section.key}>
									<Heading
										className={cn('mb-1 text-sm font-semibold', group.context ? 'text-ds-text-muted' : 'text-ds-text')}
									>
										{section.title}
									</Heading>
									<SectionFields fields={section.fields} />
								</div>
							)
						})}
					</div>
				))}
			</div>
		</section>
	)
}
